import React, { useEffect, useMemo, useState } from 'react';
import { Banknote, RefreshCw, Save, SlidersHorizontal, Table2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCondominio } from '../contexts/CondominioContext';

const Parametros = () => {
  const { getCondominioNome, getCondominioId } = useCondominio();
  const condominioId = getCondominioId();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [parametroBanco, setParametroBanco] = useState(null);
  const [bancoSelecionado, setBancoSelecionado] = useState('chave_pix');
  const [parametros, setParametros] = useState([]);
  const [abaAtiva, setAbaAtiva] = useState('gerais');
  const [pixConfig, setPixConfig] = useState(null);
  const [pixForm, setPixForm] = useState({
    gateway: 'chave_pix',
    pix_chave: '',
    fallback_nome: '',
    fallback_cidade: '',
    fallback_ativo: true,
    client_id: '',
    client_secret: '',
    cert_path: '',
    key_path: '',
    cert_passphrase: '',
    escopo: '',
    token_url: '',
    base_url: '',
    qr_expiracao_minutos: 30
  });
  const [salvandoPix, setSalvandoPix] = useState(false);

  const parametroBancoId = parametroBanco?.parametro_id;

  const carregarTudo = async () => {
    try {
      setCarregando(true);
      setErro(null);
      const [paramsRes, pixRes] = await Promise.all([
        api.get('/parametros', { params: condominioId ? { condominio_id: condominioId } : {} }),
        api.get('/pix-config', { params: condominioId ? { condominio_id: condominioId } : {} })
      ]);
      const params = paramsRes.data?.data?.parametros || [];
      setParametros(params);
      const parametroBancoPix = params.find((p) => p.parametro_nome === 'banco_integracao_pix');
      setParametroBanco(parametroBancoPix || null);
      const fallbackBanco = parametroBancoPix?.parametro_valor_texto || parametroBancoPix?.parametro_valor || null;
      const config = pixRes.data?.data?.config || null;
      aplicarPixConfig(config, fallbackBanco);
    } catch (error) {
      console.error('Erro ao carregar:', error);
      setErro(error.response?.data?.message || 'Erro ao carregar parâmetros.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarTudo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominioId]);

  const parametrosOrdenados = useMemo(() => {
    return [...parametros]
      .filter(param => param.parametro_nome !== 'banco_integracao_pix')
      .sort((a, b) => a.parametro_nome.localeCompare(b.parametro_nome));
  }, [parametros]);

  const formatarValor = (param) => {
    if (param.parametro_tipo === 'texto') {
      return param.parametro_valor_texto || '—';
    }
    if (param.parametro_tipo === 'percentual') {
      return `${Number(param.parametro_valor || 0).toFixed(2)}%`;
    }
    if (param.parametro_tipo === 'decimal') {
      return Number(param.parametro_valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
    return param.parametro_valor ?? '—';
  };

  const formatarEscopo = (param) => {
    if (!param.condominio_id) {
      return 'Global';
    }
    if (param.condominio?.condominio_nome) {
      return param.condominio.condominio_nome;
    }
    return `Condomínio ${param.condominio_id}`;
  };

  const aplicarPixConfig = (config, fallbackBanco = null) => {
    setPixConfig(config);
    const banco = config?.gateway || fallbackBanco || 'chave_pix';
    setBancoSelecionado(banco);
    setPixForm((prev) => ({
      ...prev,
      gateway: banco,
      fallback_nome: config?.fallback_nome || '',
      fallback_cidade: config?.fallback_cidade || '',
      fallback_ativo: config?.fallback_ativo ?? true,
      qr_expiracao_minutos: config?.qr_expiracao_minutos || 30
    }));
  };

  const carregarPixConfig = async (fallbackBanco = null) => {
    try {
      const response = await api.get('/pix-config', {
        params: condominioId ? { condominio_id: condominioId } : {}
      });
      const config = response.data?.data?.config || null;
      aplicarPixConfig(config, fallbackBanco);
    } catch (error) {
      console.error('Erro ao carregar configuração PIX:', error);
    }
  };

  const handlePixInputChange = (field, value) => {
    setPixForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const salvarPixConfig = async () => {
    try {
      setSalvandoPix(true);
      const payload = {
        condominio_id: condominioId,
        gateway: 'chave_pix',
        fallback_nome: pixForm.fallback_nome,
        fallback_ativo: true,
        pix_chave: pixForm.pix_chave || undefined
      };

      await api.put('/pix-config', payload);
      const bancoParaParametro = 'chave_pix';
      // Manter banco selecionado - não recarregar para evitar sobrescrever com resposta incorreta
      setBancoSelecionado(bancoParaParametro);
      setPixForm((prev) => ({ ...prev, gateway: bancoParaParametro }));
      try {
        const payloadParam = {
          parametro_nome: 'banco_integracao_pix',
          parametro_tipo: 'texto',
          parametro_valor_texto: bancoParaParametro,
          parametro_descricao: 'Define qual banco será utilizado para gerar QR Code PIX dos boletos'
        };
        if (parametroBancoId) {
          await api.put(`/parametros/${parametroBancoId}`, payloadParam);
        } else {
          await api.post('/parametros', { ...payloadParam, condominio_id: condominioId });
        }
      } catch (e) {
        console.warn('Erro ao atualizar parâmetro banco_integracao_pix:', e);
      }
    } catch (error) {
      console.error('Erro ao salvar configuração PIX:', error);
      toast.error(error.response?.data?.message || 'Não foi possível salvar as configurações PIX.');
    } finally {
      setSalvandoPix(false);
    }
  };

  useEffect(() => {
    carregarPixConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominioId]);

  const Tabs = (
    <div className="mt-6 border-b border-gray-200">
      <nav className="-mb-px flex flex-wrap gap-4">
        {[
          { id: 'gerais', label: 'Gerais', icon: SlidersHorizontal },
          { id: 'pix', label: 'PIX', icon: Banknote }
        ].map((tab) => {
          const Icon = tab.icon;
          const ativo = abaAtiva === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setAbaAtiva(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition ${
                ativo ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );

  if (carregando) {
    return (
      <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-sm">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm text-gray-500">Condomínio ativo</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {getCondominioNome()}
            </h1>
            <p className="text-gray-500 mt-1">
              Configure qual banco será utilizado para gerar QR Code PIX dos
              boletos emitidos neste condomínio.
            </p>
          </div>
          <button
            onClick={carregarTudo}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
        </div>
      </div>

      {Tabs}

      {abaAtiva === 'gerais' && (
        <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Table2 className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Parâmetros cadastrados</h2>
            </div>
            <p className="text-sm text-gray-500">
              Valores específicos do condomínio são priorizados em relação aos globais.
            </p>
          </div>
          {parametrosOrdenados.length === 0 ? (
            <p className="text-gray-500">Nenhum parâmetro encontrado para este condomínio.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Parâmetro</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Valor</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Tipo</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Escopo</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Atualizado em</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {parametrosOrdenados.map((parametro) => (
                    <tr key={parametro.parametro_id}>
                      <td className="px-4 py-3 font-semibold text-gray-900 capitalize">
                        {parametro.parametro_nome.replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{formatarValor(parametro)}</td>
                      <td className="px-4 py-3 text-gray-500">{parametro.parametro_tipo}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                          {formatarEscopo(parametro)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {parametro.parametro_dt_atualizacao
                          ? new Date(parametro.parametro_dt_atualizacao).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {abaAtiva === 'pix' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 max-w-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-primary-50 text-primary-600 p-2 rounded-lg">
              <Banknote className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">PIX do condomínio</h2>
              <p className="text-sm text-gray-500">
                {getCondominioNome() || 'Condomínio selecionado'}. Cada condomínio tem a própria chave.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Nome do recebedor</label>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border-gray-200"
                value={pixForm.fallback_nome}
                onChange={(event) => handlePixInputChange('fallback_nome', event.target.value)}
                placeholder="Nome que aparece no PIX"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Chave PIX</label>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border-gray-200"
                placeholder={pixConfig?.pix_chave_mascarado || 'CPF, e-mail, telefone ou chave aleatória'}
                value={pixForm.pix_chave}
                onChange={(event) => handlePixInputChange('pix_chave', event.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">Deixe a chave em branco para manter a atual.</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={salvarPixConfig}
              disabled={salvandoPix}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-white font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {salvandoPix ? (
                <>
                  <LoadingSpinner size="sm" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar
                </>
              )}
            </button>
            {pixConfig?.atualizado_em && (
              <p className="text-xs text-gray-500">
                Última atualização:{' '}
                {new Date(pixConfig.atualizado_em).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Parametros;


